import React, {useEffect, useState} from 'react';
import {Sidebar} from "./components/Sidebar";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import {useNavigate, useParams} from "react-router-dom";

// pages
import UserManagementPage from "./UserManagementPage";
import DashboardPage from "./DashboardPage";
import CaseManagementPage from "./CaseManagementPage";
import OrganizationManagementPage from "./OrganizationManagementPage";
import SystemInfoPage from "./SystemInfoPage";
import MedicalOrganizationManagementPage from "./MedicalOrganizationManagementPage";
import DatabaseManagementPage from "./DatabaseManagementPage";
import ContactPage from "./ContactPage";
import ContentManagementPage from "./ContentManagementPage";
import RobotManagementPage from "./RobotManagementPage";
import MeasurementManagementPage from "./MeasurementManagementPage";
import StoryboardPage from "./StoryboardPage";
import AuditLogPage from "./AuditLogPage";
import BsrsManagementPage from "./BsrsManagementPage";

// services
import {getUser} from "../../services/user";
import {getOrganizationById} from "../../services/organization";
import {ActionButton} from "../../designSystem/components/ActionButton";

const AdminPage = () => {
  const [userName, setUserName] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const [userPermissions, setUserPermissions] = useState<object>({});
  const [userOrganizationNames, setUserOrganizationNames] = useState<string[]>([]);
  const [userOrganizationGroup, setUserOrganizationGroup] = useState<string[]>([]);
  const [userToken, setUserToken] = useState<string>('');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const {content} = useParams();

  useEffect(() => {
    // Check user authentication status
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Fetch user info from Firestore
        const token = await currentUser.getIdToken();
        const userData = await getUser({ token, id: currentUser.uid });

        if (userData) {
          const org = await getOrganizationById({ token, id: userData?.organization })
          const organizationGroups: string[] = org['group'];
          const organizationNames: string = org['name'];

          setUserToken(token)
          setUserOrganizationNames([organizationNames])
          setUserOrganizationGroup(organizationGroups)
          setUserRole(userData.role);
          setUserName(userData.name || "未知使用者");
          setUserPermissions(userData.permissions || {});
        } else {
          setUserName("未知使用者");
        }
      } else {
        navigate("/login"); // Redirect to login if not logged in
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  return (
    <div className="flex flex-col h-screen">
      {/* Top Navbar */}
      <div className="flex items-center justify-between py-2 pl-4 md:hidden" style={{backgroundColor: "rgba(55, 125, 157, 1)"}}>
        <ActionButton
          dark
          iconName="fa-bars"
          variant="ghost"
          onClick={() => setMenuOpen(prev => !prev)}
        />
        <img src={"/img/logo.png"} alt="logo" className="w-[200px]" />
        <div className={"w-[40px]"} />
      </div>

      {/* Mobile Sidebar */}
      <div
        className={`
          fixed top-0 left-0 h-full bg-white shadow-md transform transition-transform duration-300 z-50 w-64
          md:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}          
        `}
        style={{
          boxShadow: (menuOpen) ? "10px 0 8px rgba(0, 0, 0, 0.15)" : null
        }}
      >
        <Sidebar
          mode="mobile"
          onClickLogo={() => setMenuOpen(prev => !prev)}
          userOrganizationNames={userOrganizationNames}
          userName={userName}
          userRole={userRole}
          userPermissions={userPermissions}
        />
      </div>

      {/* Desktop layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar on desktop only */}
        <div className="hidden md:block">
          <Sidebar
            mode="desktop"
            userOrganizationNames={userOrganizationNames}
            userName={userName}
            userRole={userRole}
            userPermissions={userPermissions}
          />
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          {content === 'dashboard' && (
            <DashboardPage userToken={userToken} userRole={userRole} />
          )}
          {content === 'userManagement' && (
            <UserManagementPage userRole={userRole} userToken={userToken} />
          )}
          {content === 'organizationManagement' && (
            <OrganizationManagementPage userToken={userToken} userRole={userRole} userOrganizationGroup={userOrganizationGroup} />
          )}
          {content === 'caseManagement' && (
            <CaseManagementPage userToken={userToken} userRole={userRole} />
          )}
          {content === 'medicalOrganizationManagement' && (
            <MedicalOrganizationManagementPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'databaseManagement' && (
            <DatabaseManagementPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'contentManagement' && (
            <ContentManagementPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'robotManagement' && (
            <RobotManagementPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'measurementManagement' && (
            <MeasurementManagementPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'contact' && (
            <ContactPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'storyBoard' && (
            <StoryboardPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'systemInfo' && (
            <SystemInfoPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'auditLog' && (
            <AuditLogPage userToken={userToken} userPermissions={userPermissions} />
          )}
          {content === 'bsrs' && (
            <BsrsManagementPage userToken={userToken} userPermissions={userPermissions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
